/**
 * @file 图表浮窗选区状态 Hook：本地勾选值、反推勾选、confirm 与关闭兜底 refs。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { OptionsItemType, RegionSelectValueType } from '@home/components/NewRegionSelect/type';
import ChartDashboardIcon from '@/pages/aigc/assets/svg/chart-dashboard.svg';
import defaultGameIcon from '@/pages/dashboard/assets/svg/console-game-default.svg';
import type { ISelectedChartTagData } from '../components/SelectedChartTag';
import {
  buildChartTagData,
  buildOpenPanelValue,
  resolveDefaultSelectedRegion,
} from '../utils/selectionState';
import { EMPTY_VALUE } from './chartPopoverConstants';

/** useChartPopoverSelection 入参 */
interface IUseChartPopoverSelectionParams {
  /** 外部已选图表 tag，用于反推浮窗勾选态 */
  selectedCharts: ISelectedChartTagData[];
  /** 当前游戏下的 dashboard / 图表树 */
  dashboardOptions: OptionsItemType[];
  /** 主游戏列表，用于构建 tag 展示信息 */
  mainGames: unknown[];
  /** 当前语言，用于 tag 文案 */
  locale: string;
  /** 关闭或切换游戏时回传选中结果 */
  onConfirm?: (selectedCharts: ISelectedChartTagData[], gameCode: string) => void;
  /** 当前操作的游戏 code */
  selectedGameCode: string;
}

/** useChartPopoverSelection 返回值 */
interface IUseChartPopoverSelectionResult {
  /** 浮窗内 NewRegionSelect 受控选区 */
  localSelectValue: RegionSelectValueType;
  /** 更新浮窗内选区 */
  setLocalSelectValue: React.Dispatch<React.SetStateAction<RegionSelectValueType>>;
  /** 左侧默认高亮的 region value */
  defaultSelectedRegion: string;
  /** 强制 remount NewRegionSelect 的 key */
  regionMountKey: number;
  /** 按外部 selectedCharts 反推并写入本地选区 */
  applySelection: (params: {
    gameCode: string;
    dashboardValue?: string;
    autoSelect: boolean;
  }) => void;
  /** 将当前选区 confirm 给外部 onConfirm */
  confirmCurrentGame: (value: RegionSelectValueType, gameCode?: string) => void;
  /** 清空本地选区（跨游戏切换前） */
  resetLocalSelectValue: () => void;
  /** confirmCurrentGame 最新引用，供关闭 effect 同步读取 */
  confirmCurrentGameRef: React.MutableRefObject<
  (value: RegionSelectValueType, gameCode?: string) => void
  >;
  /** localSelectValue 最新引用，供关闭 effect 同步读取 */
  localSelectValueRef: React.MutableRefObject<RegionSelectValueType>;
  /** selectedGameCode 最新引用，供关闭 effect 同步读取 */
  selectedGameCodeRef: React.MutableRefObject<string>;
}

/**
 * 管理图表浮窗选区状态与 confirm 逻辑。
 *
 * - 维护 `localSelectValue` / `defaultSelectedRegion` / `regionMountKey`
 * - 提供 `applySelection` 反推勾选、`confirmCurrentGame` 回传外部
 * - 通过 ref 同步最新值，避免关闭 effect 闭包陈旧
 *
 * @param params - 选区 Hook 入参
 * @returns 选区状态、操作方法与关闭兜底 refs
 */
const useChartPopoverSelection = ({
  selectedCharts,
  dashboardOptions,
  mainGames,
  locale,
  onConfirm,
  selectedGameCode,
}: IUseChartPopoverSelectionParams): IUseChartPopoverSelectionResult => {
  /** 浮窗内 NewRegionSelect 受控值 */
  const [localSelectValue, setLocalSelectValue] = useState<RegionSelectValueType>(EMPTY_VALUE);
  /** 左侧栏默认高亮项 */
  const [defaultSelectedRegion, setDefaultSelectedRegion] = useState<string>('');
  /** 变更选区后递增，强制 remount 刷新左侧高亮 */
  const [regionMountKey, setRegionMountKey] = useState(0);

  /**
   * 将当前选区打包为 tag 数据并触发 onConfirm。
   * @param value - 当前 RegionSelect 选区
   * @param gameCode - 目标游戏 code，默认取 selectedGameCode
   */
  const confirmCurrentGame = useCallback((
    value: RegionSelectValueType,
    gameCode = selectedGameCode,
  ) => {
    onConfirm?.(
      buildChartTagData(
        value,
        dashboardOptions,
        gameCode,
        mainGames,
        locale,
        defaultGameIcon,
        ChartDashboardIcon,
      ),
      gameCode,
    );
  }, [dashboardOptions, mainGames, locale, onConfirm, selectedGameCode]);

  /**
   * 按外部 selectedCharts 反推勾选态并写入本地 state。
   * autoSelect=true 时立即 confirm，用于自定义报表等外部入口。
   * @param params - 目标游戏、可选 dashboard 高亮、是否立即 confirm
   */
  const applySelection = useCallback((params: {
    gameCode: string;
    dashboardValue?: string;
    autoSelect: boolean;
  }) => {
    const nextValue = buildOpenPanelValue({
      selectedCharts,
      dashboardOptions,
      gameCode: params.gameCode,
      dashboardValue: params.dashboardValue,
      autoSelect: params.autoSelect,
    });

    setDefaultSelectedRegion(resolveDefaultSelectedRegion({
      value: nextValue,
      dashboardOptions,
      dashboardValue: params.dashboardValue,
    }));
    setLocalSelectValue(nextValue);
    setRegionMountKey((prev) => prev + 1);

    if (params.autoSelect) {
      confirmCurrentGame(nextValue, params.gameCode);
    }
  }, [selectedCharts, dashboardOptions, confirmCurrentGame]);

  /** 跨游戏切换前清空本地选区，等待目标游戏数据就绪后由 pending effect 回填 */
  const resetLocalSelectValue = useCallback(() => {
    setLocalSelectValue(EMPTY_VALUE);
  }, []);

  /** 关闭兜底：读取 confirm 最新实现 */
  const confirmCurrentGameRef = useRef(confirmCurrentGame);
  /** 关闭兜底：读取选区最新值 */
  const localSelectValueRef = useRef(localSelectValue);
  /** 关闭兜底：读取游戏 code 最新值 */
  const selectedGameCodeRef = useRef(selectedGameCode);

  /** 每次渲染同步 ref，供 opened=false 的 effect 无闭包滞后地 confirm */
  useEffect(() => {
    confirmCurrentGameRef.current = confirmCurrentGame;
    localSelectValueRef.current = localSelectValue;
    selectedGameCodeRef.current = selectedGameCode;
  });

  return {
    localSelectValue,
    setLocalSelectValue,
    defaultSelectedRegion,
    regionMountKey,
    applySelection,
    confirmCurrentGame,
    resetLocalSelectValue,
    confirmCurrentGameRef,
    localSelectValueRef,
    selectedGameCodeRef,
  };
};

export default useChartPopoverSelection;
